import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Download, Smartphone, Globe, Chrome } from "lucide-react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface AppDownloadButtonProps {
  variant?: "default" | "outline" | "secondary" | "hero" | "orange";
  size?: "sm" | "default" | "lg" | "xl";
  className?: string;
  label?: string; // optional compact label
}

export const AppDownloadButton = ({ variant = "default", size = "default", className, label }: AppDownloadButtonProps) => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showInstallDialog, setShowInstallDialog] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches || 
        (window.navigator as any).standalone === true) {
      setIsInstalled(true);
      return;
    }

    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // Listen for the beforeinstallprompt event (Android/Chrome)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Android/Chrome installation
      try {
        await deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        
        if (outcome === 'accepted') {
          setDeferredPrompt(null);
          setShowInstallDialog(false);
        }
      } catch (error) {
        console.error('Installation failed:', error);
      }
    } else {
      // Show instructions dialog
      setShowInstallDialog(true);
    }
  };

  // Don't show button if already installed
  if (isInstalled) {
    return null;
  }

  return (
    <>
      <Button
        onClick={handleInstallClick}
        variant={variant}
        size={size}
        className={className}
      >
        <Download className="h-4 w-4 mr-2" />
        {label ?? "Download Our App"}
      </Button>

      <Dialog open={showInstallDialog} onOpenChange={setShowInstallDialog}>
        <DialogContent className="w-[95vw] sm:max-w-md mx-2 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5" />
              Install Sortable App
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="text-center">
              <Download className="h-12 w-12 text-primary mx-auto mb-3" />
              <p className="text-muted-foreground">
                Get faster access to Sortable! Install our app for a better experience.
              </p>
            </div>

            {isIOS ? (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    Install on iOS
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="flex items-start gap-2">
                    <span className="bg-primary text-white rounded-full w-5 h-5 text-xs flex items-center justify-center mt-0.5">1</span>
                    <p>Tap the Share button <span className="font-mono">⎋</span> at the bottom of your screen</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="bg-primary text-white rounded-full w-5 h-5 text-xs flex items-center justify-center mt-0.5">2</span>
                    <p>Scroll down and tap "Add to Home Screen"</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="bg-primary text-white rounded-full w-5 h-5 text-xs flex items-center justify-center mt-0.5">3</span>
                    <p>Tap "Add" to install Sortable on your home screen</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="text-center space-y-2">
                <p className="text-sm text-muted-foreground">
                  For the best experience, use Chrome or Edge browser and look for the install prompt.
                </p>
                <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                  <Chrome className="h-3 w-3" />
                  Works best in Chrome
                </div>
              </div>
            )}

            <div className="flex justify-center pt-2">
              <Button variant="outline" onClick={() => setShowInstallDialog(false)}>
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};